<div class="field-analysis-container">
  <div class="left-column">
    <div class="ndvi-chart-container">
      <app-ndvi-chart [fieldId]="fieldId"></app-ndvi-chart>
    </div>
    <div class="crop-spray-chart-container">
      <app-crop-spray-analysis [fieldId]="fieldId"></app-crop-spray-analysis>
    </div>
    <div class="crop-bio-chart-container">
      <app-crop-bio [fieldId]="fieldId"></app-crop-bio>
    </div>
    <div class="micronutrients-chart-container">
      <app-micronutrients [fieldId]="fieldId"></app-micronutrients>
    </div>
  </div>
  <div class="right-column">
    <div class="info-card">
      <!-- Southern Gateway only, beside the field outline. The status and the drawer belong to the
           dashboard page. title rather than matTooltip: this page's CSS hides Material tooltips. -->
      <!-- A chip rather than a "?" (Heinrich, 28/10/2026, option B): the status reads as text, so it never
           relies on the dot's colour, and the tint follows the same tones as the drawer's pills. -->
      <button *ngIf="dashboard?.sg" [id]="statusButtonId" type="button" (click)="dashboard!.toggleDrawer()"
              class="integration-status-slot inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border pl-2.5 pr-1.5 text-xs font-medium text-[var(--base-text-alt-dark)]"
              [ngClass]="dashboard!.statusDot() ? dashboard!.chipClasses(dashboard!.statusDot()!.tone) : 'border-[var(--app-border)] bg-[var(--app-base)]'"
              aria-controls="integration-drawer" [attr.aria-expanded]="dashboard!.drawerOpen()"
              [attr.aria-label]="'Integration status for this field: ' + (dashboard!.statusDot()?.label || 'loading')"
              [title]="'Integration status: ' + (dashboard!.statusDot()?.label || 'loading')"><span
              aria-hidden="true" data-status-dot class="h-2 w-2 shrink-0 rounded-full"
              [ngClass]="dashboard!.statusDot() ? dashboard!.dotClasses(dashboard!.statusDot()!.tone) : 'bg-[var(--base-variant)]'"></span><span
              data-status-text>Integrations: {{ (dashboard!.statusDot()?.label || 'loading') | lowercase }}</span><span
              class="material-icons text-[16px]! leading-none" aria-hidden="true">chevron_right</span></button>
      <p><strong class="field-analysis-header">Field Details - {{ fieldName }}</strong></p>
      <p><strong>Crop: </strong><span *ngIf="!fieldInfoLoading">{{ crop }}</span><span *ngIf="fieldInfoLoading" class="skeleton-text"></span></p>
      <p><strong>Cultivar: </strong><span *ngIf="!fieldInfoLoading">{{ cultivar }}</span><span *ngIf="fieldInfoLoading" class="skeleton-text"></span></p>
      <p><strong>Season:</strong> {{ seasonYear }}</p>
      <!-- Rendered unconditionally so the labels and their loaders are visible while the
           sync service answers. Three states per row: loading, unavailable (the call failed),
           and known. "Unavailable" is deliberately not "not linked". -->
      <p class="rest-break">
        <strong>Rest break: </strong>
        <span *ngIf="fieldContextLoading" class="skeleton-text skeleton-text-wide"></span>
        <span *ngIf="!fieldContextLoading && fieldContextError" class="not-linked">unavailable</span>
        <ng-container *ngIf="!fieldContextLoading && !fieldContextError">
          <ng-container *ngIf="fieldContext?.restBreak">
            <!-- The applied date leads whenever there is one, the tag says how it went against
                 the plan, and the product sits alongside rather than on a line of its own. -->
            <span class="rb-date">{{ (fieldContext.restBreak.actualDate || fieldContext.restBreak.plannedDate) | date:'yyyy-MM-dd' }}</span>
            <span class="rb-tag"
                  [class.rb-applied]="fieldContext.restBreak.actualDate"
                  [class.rb-planned]="!fieldContext.restBreak.actualDate"
                  [title]="'Planned ' + (fieldContext.restBreak.plannedDate | date:'yyyy-MM-dd')">{{ restBreakTag }}</span>
            <span class="rb-product">{{ fieldContext.restBreak.productName }}</span>
          </ng-container>
          <span *ngIf="!fieldContext?.restBreak" class="not-linked">no record for this season</span>
        </ng-container>
      </p>
      <!-- Data links. Each row is one button that opens the editor on that system's tab, with a
           pencil and Link/Edit at its end so it reads as editable. Loading and unavailable stay
           plain rows: there is nothing to edit until the context has loaded. -->
      <div class="field-links">
        <ng-container *ngIf="!fieldContextLoading && !fieldContextError && fieldContext; else linksPending">
          <button type="button" class="link-row" (click)="openLinkEditor('fms')"
                  [attr.aria-label]="(fieldContext.fms.linked ? 'Edit' : 'Link') + ' FMS block'">
            <strong>FMS:</strong>
            <span *ngIf="fieldContext.fms.linked" class="link-pill border-[var(--notification-success)] bg-[var(--notification-success)]/15">Linked, block {{ fieldContext.fms.blockNo }}</span>
            <span *ngIf="!fieldContext.fms.linked" class="link-pill border-[var(--notification-warning)] bg-[var(--notification-warning)]/25">Not linked</span>
            <ng-container *ngTemplateOutlet="linkSource; context: { $implicit: fieldContext.fms }"></ng-container>
            <span class="link-action"><mat-icon>edit</mat-icon>{{ fieldContext.fms.linked ? 'Edit' : 'Link' }}</span>
          </button>
          <button type="button" class="link-row" (click)="openLinkEditor('igator')"
                  [attr.aria-label]="(fieldContext.igator.linked ? 'Edit' : 'Link') + ' iGator taps'">
            <strong>iGator:</strong>
            <span *ngIf="fieldContext.igator.linked" class="link-pill border-[var(--notification-success)] bg-[var(--notification-success)]/15">Linked, {{ fieldContext.igator.tapCount }} tap{{ fieldContext.igator.tapCount === 1 ? '' : 's' }}</span>
            <span *ngIf="!fieldContext.igator.linked" class="link-pill border-[var(--notification-warning)] bg-[var(--notification-warning)]/25">Not linked</span>
            <ng-container *ngTemplateOutlet="linkSource; context: { $implicit: fieldContext.igator }"></ng-container>
            <span class="link-action"><mat-icon>edit</mat-icon>{{ fieldContext.igator.linked ? 'Edit' : 'Link' }}</span>
            <!-- A full-width line under the pill, wrapping: a mis-mapped field can carry 14 taps. -->
            <span class="tap-list link-taps" *ngIf="fieldContext.igator.linked">
              <span class="tap" *ngFor="let tap of fieldContext.igator.taps" [title]="tapTitles[tap] || ''">{{ tap }}</span>
            </span>
          </button>
        </ng-container>
        <ng-template #linksPending>
          <p>
            <strong>FMS: </strong>
            <span *ngIf="fieldContextLoading" class="skeleton-text"></span>
            <span *ngIf="!fieldContextLoading" class="not-linked">unavailable</span>
          </p>
          <p>
            <strong>iGator: </strong>
            <span *ngIf="fieldContextLoading" class="skeleton-text"></span>
            <span class="tap-list tap-inline" *ngIf="fieldContextLoading">
              <span class="skeleton-chip"></span>
              <span class="skeleton-chip"></span>
            </span>
            <span *ngIf="!fieldContextLoading" class="not-linked">unavailable</span>
          </p>
        </ng-template>
        <!-- How the link came about. "Automatic" only when the sync actually matched something; an
             unlinked field the sync found nothing for says so instead. -->
        <ng-template #linkSource let-link>
          <span class="whitespace-nowrap text-xs text-[var(--tertiary-text)]">
            <ng-container *ngIf="link.manual">Manual<ng-container *ngIf="link.lastManualChange"> · {{ link.lastManualChange.userName || 'unknown user' }} · {{ link.lastManualChange.at | date:'yyyy-MM-dd' }}</ng-container></ng-container>
            <ng-container *ngIf="!link.manual && link.linked">Automatic</ng-container>
            <ng-container *ngIf="!link.manual && !link.linked">No match found by the sync</ng-container>
          </span>
        </ng-template>
      </div>
      <app-field-map [fieldId]="fieldId" [farmId]="farmId"></app-field-map>
      <button class="map-analysis-btn" (click)="navigateToFieldAnalysis()">
        Map Analysis
        <mat-icon>visibility</mat-icon>
      </button>
    </div>
    <div class="irrigation-chart-container-bottom">
      <app-irrigation-chart [fieldId]="fieldId"></app-irrigation-chart>
    </div>
    <div class="macronutrients-chart-container">
      <app-macronutrients [fieldId]="fieldId"></app-macronutrients>
    </div>
    <div class="gibberellin-chart-container">
      <app-gibberellin [fieldId]="fieldId"></app-gibberellin>
    </div>
  </div>
</div>
<app-field-link-editor [open]="linkEditorOpen" [fieldId]="fieldId" [fieldName]="fieldName" [context]="fieldContext"
                       [initialTab]="linkEditorTab"
                       (closed)="onLinkEditorClosed($event)"></app-field-link-editor>
